import {OverlayPanel} from "primereact/overlaypanel";
import {useEffect, useLayoutEffect, useRef} from "react";
import {useDispatch} from "react-redux";

import {Map, Overlay} from "ol";
import ScaleLine from "ol/control/ScaleLine";
import {DragPan, Draw} from "ol/interaction";
import {toLonLat} from "ol/proj";
import LayerSwitcher from "ol-layerswitcher";

import {useRevaTranslation} from "../hooks/languageHooks";
import {hidePdfPreview} from "../redux/pdfPreviewSlice";
import {downloadPdfForType} from "../redux/pdfThunk";
import {
    createLayers,
    createView,
    logMapDebug,
    registerCRSs,
    resolutionFromScale,
    roundScaleForDisplay,
    scaleFromResolution,
    updateLayers,
    updateView,
} from "../util/maps";
import OpenLayersLegendControl from "./OpenLayersLegendControl";
import OpenLayersPinControl from "./OpenLayersPinControl";

// Register EPSG:31467 and EPSG:3857 once on loading of this module
registerCRSs();

/**
 * ScaleLine, deren angezeigter Maßstabsnenner auf glatte Hunderter gerundet wird
 * (letzte 2 Stellen -> "00"). Die Kartenauflösung/Distanz-Beschriftung bleibt
 * unverändert; nur die "1 : X"-Zahl wirkt rund. Siehe roundScaleForDisplay.
 */
class RoundedScaleLine extends ScaleLine {
    getScaleForResolution() {
        return roundScaleForDisplay(super.getScaleForResolution());
    }
}

// Maßstabs-Elemente: der Balken (bar/line) und das Klick-Eingabe-Popup. Über
// diesen soll KEIN Hover-Tooltip der Karte erscheinen (er liegt sonst über der
// Skala und passt inhaltlich nicht dorthin).
const SCALE_ELEMENT_SELECTOR = ".ol-scale-bar, .ol-scale-line, .scale-popup";

/**
 * Prüft, ob der Zeiger gerade über einem Maßstabs-Element liegt (Balken/Popup).
 * @param {import("ol/MapBrowserEvent").default} evt
 * @returns {boolean}
 */
function isPointerOverScale(evt) {
    const target = evt?.originalEvent?.target;
    return !!(target && typeof target.closest === "function" && target.closest(SCALE_ELEMENT_SELECTOR));
}

/**
 * Ermittelt den Browser-Standard-Shortcut zum Verkleinern der Ansicht
 * („Zoom out"), damit im Hinweis der zum aktuellen Browser passende Tastenkürzel
 * steht. Alle großen Browser nutzen Strg/Cmd + Minus; unterschieden wird daher im
 * Wesentlichen macOS (⌘) vs. Windows/Linux (Strg) sowie der Browsername für die
 * drei wichtigsten Browser (Chrome, Firefox, Safari; zusätzlich Edge).
 * @return {{name: string, shortcut: string}}
 */
function getBrowserZoomOutShortcut() {
    // Tastenkürzel mit geschützten Leerzeichen (U+00A0) zusammenhalten, damit es nicht
    // über mehrere Zeilen umbricht. „Minus" ausgeschrieben (eindeutiger als „-"/„−").
    const shortcutFor = (mod) => `${mod}\u00a0+\u00a0Minus`;
    if (typeof navigator === "undefined") return {name: "Browser", shortcut: shortcutFor("Strg")};
    const ua = navigator.userAgent || "";
    const isMac = /Mac|iP(hone|ad|od)/.test(navigator.platform || ua);
    const mod = isMac ? "⌘" : "Strg";
    let name = "diesem Browser";
    if (/Firefox\//.test(ua)) name = "Firefox";
    else if (/Edg\//.test(ua)) name = "Edge";
    else if (/Chrome\//.test(ua)) name = "Chrome";
    else if (/Safari\//.test(ua)) name = "Safari";
    return {name, shortcut: shortcutFor(mod)};
}

/**
 * Injiziert einmalig die CSS-Keyframes für den „atmenden" PDF-Druckbereich
 * (pulsierende Transparenz während der PDF-Generierung).
 */
function ensurePdfPreviewBreatheKeyframes() {
    if (typeof document === "undefined") return;
    if (document.getElementById("reva-pdf-preview-breathe")) return;
    const style = document.createElement("style");
    style.id = "reva-pdf-preview-breathe";
    // Pulsiert die FÜLLFLÄCHE (background) zwischen ca. 35% und 60% Deckung — der
    // rote Rahmen bleibt solide (nicht die Gesamt-Opacity animieren).
    style.textContent =
        "@keyframes revaPdfPreviewBreathe { 0%, 100% { background-color: color-mix(in srgb, var(--primary-color) 35%, transparent); } 50% { background-color: color-mix(in srgb, var(--primary-color) 60%, transparent); } }";
    document.head.appendChild(style);
}

/**
 * (De)aktiviert das Panning (DragPan) einer OpenLayers-Karte — während der
 * PDF-Generierung soll die Karte nicht verschoben werden können.
 * @param {import("ol").Map} map
 * @param {boolean} active
 */
function setDragPanActive(map, active) {
    map.getInteractions().forEach((interaction) => {
        if (interaction instanceof DragPan) interaction.setActive(active);
    });
}

/**
 * Build the scale-popup DOM element and wire up its behaviour.
 */
function createScalePopup() {
    const container = document.createElement("div");
    container.className = "scale-popup";
    container.style.display = "none";

    const label = document.createElement("label");
    label.textContent = "1 :";
    label.htmlFor = "scale-input";

    const input = document.createElement("input");
    input.id = "scale-input";
    input.type = "text";
    input.inputMode = "numeric";
    input.pattern = "[0-9]*";

    container.appendChild(label);
    container.appendChild(input);

    container.addEventListener("mousedown", (e) => e.stopPropagation());

    /** @type {Map|null} */
    let activeMap = null;

    function hide() {
        container.style.display = "none";
        activeMap = null;
    }

    function applyScale() {
        const value = parseInt(input.value, 10);
        if (!value || value <= 0 || !activeMap) {
            hide();
            return;
        }
        const view = activeMap.getView();
        const center = view.getCenter();
        if (!center) {
            hide();
            return;
        }
        const [, lat] = toLonLat(center);
        view.setResolution(resolutionFromScale(value, lat));
        hide();
    }

    input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
            applyScale();
        } else if (e.key === "Tab") {
            e.preventDefault();
            applyScale();
        } else if (e.key === "Escape") {
            hide();
            document.removeEventListener("mousedown", handleOutsideClick);
        }
    });

    function handleOutsideClick() {
        applyScale();
        document.removeEventListener("mousedown", handleOutsideClick);
    }

    /**
     * Show the popup above the given ScaleLine element.
     * @param {Map} map
     * @param {HTMLElement} scaleLineEl
     */
    function show(map, scaleLineEl) {
        activeMap = map;
        const view = map.getView();
        const resolution = view.getResolution();
        const center = view.getCenter();
        if (resolution == null || !center) return;
        const [, lat] = toLonLat(center);
        input.value = String(roundScaleForDisplay(scaleFromResolution(resolution, lat)));

        // Position directly above the ScaleLine
        const viewport = map.getViewport();
        const viewportRect = viewport.getBoundingClientRect();
        const scaleRect = scaleLineEl.getBoundingClientRect();
        container.style.left = `${scaleRect.left - viewportRect.left + 70}px`;
        container.style.bottom = `${viewportRect.bottom - scaleRect.top + 26}px`;
        container.style.display = "flex";

        // Focus & select after showing
        requestAnimationFrame(() => {
            input.focus();
            input.select();
        });

        setTimeout(() => {
            document.addEventListener("mousedown", handleOutsideClick);
        }, 0);
    }

    return {element: container, show, hide};
}

/**
 * React Hook to use a OL Map.
 * @param {MutableRefObject<HTMLDivElement|null>} mapDivRef ref to the DIV containing the map
 * @param {MapModel} mapModel map model describing the properties of the map
 * @param {MutableRefObject<OverlayPanel|null>} tooltipOverlayRef ref to the tooltip content, or null for no reference
 * @param {MutableRefObject<OverlayPanel|null>|null} legendOverlayRef ref to the legend content, or null for no reference
 * @param {LegendOptions|null} legendOptions is the legend visible initially ?
 * @param {PdfPreviewInfo|null} [pdfPreview] optionale PDF-Karten-Vorschau (Pixelmaße + nonce)
 * @return {MutableRefObject<Map|null>} reference for map
 */
/** Kachel-Lade-Fortschrittsbalken: erst nach dieser Verzögerung einblenden (schnelle/gecachte Loads → kein Balken). */
const TILE_PROGRESS_DELAY_MS = 200;
/** Hard-Cap: Anzeige spätestens nach dieser Zeit ausblenden (ab dann wird's peinlich). */
const TILE_PROGRESS_MAX_MS = 30000;
/** Fortschritt wird in so viele Schritte quantisiert (10-Segment-Anzeige). */
const TILE_PROGRESS_STEPS = 10;
/**
 * Nicht-lineare Gewichtung der Schritte (Anteil^γ, γ>1): die ERSTEN Segmente brauchen mehr echten
 * Fortschritt (dauern länger), die HINTEREN füllen schnell. So landet man bei „letzte Kachel fehlt"
 * z.B. bei ~80% statt beim frustrierenden 99%-Phänomen.
 */
const TILE_PROGRESS_GAMMA = 2.6;

function useOpenLayersMap(mapDivRef, mapModel, tooltipOverlayRef, legendOverlayRef, legendOptions, pdfPreview) {
    /** @type {MutableRefObject<Map|null>} */
    const mapRef = useRef(null);
    /** @type {MutableRefObject<import("ol/interaction/Draw").default|null>} aktuell an der Karte hängende Draw-Interaction, falls das mapModel gerade eine anbietet */
    const activeInteractionRef = useRef(null);
    /** @type {MutableRefObject<import("ol/layer/Vector").default|null>} zur aktiven Interaction gehörender Vector-Layer (falls angegeben) */
    const activeInteractionLayerRef = useRef(null);
    /** @type {MutableRefObject<OpenLayersLegendControl|null>} */
    const legendControlRef = useRef(null);
    /** @type {MutableRefObject<HTMLDivElement|null>} DOM-Overlay für die rote PDF-Druckbereich-Box */
    const pdfPreviewElRef = useRef(null);
    /** @type {MutableRefObject<number|null>} Timer bis zum Ausblend-Start */
    const pdfPreviewFadeStartRef = useRef(null);
    /** @type {MutableRefObject<number|null>} Timer bis zum Ausblenden (display:none) */
    const pdfPreviewHideRef = useRef(null);
    /** @type {MutableRefObject<HTMLElement[]>} Maßstabs-Elemente (Balken + Popup), über/um die kein Tooltip erscheinen soll */
    const scaleRegionElsRef = useRef([]);
    /** @type {MutableRefObject<boolean>} true während der PDF-Generierung — steuert „atmen", Pan-/Tooltip-Sperre und „direkt ausblenden am Ende" */
    const pdfGeneratingRef = useRef(false);
    /**
     * Event-Handler des aktuellen Renders. Die OL-Listener werden nur einmal beim Erzeugen der Karte
     * registriert und lesen die Handler über diese Ref, sonst riefen sie dauerhaft die Closures des
     * ersten Renders auf (veralteter State, falsches Thema beim Persistieren, #19447).
     * @type {MutableRefObject<Object|null|undefined>}
     */
    const eventHandlersRef = useRef(null);
    eventHandlersRef.current = mapModel?.eventHandlers;

    // --- Kachel-Lade-Spinner (dezent, nicht blockierend) ---
    /** @type {MutableRefObject<HTMLElement|null>} Spinner-Overlay-Element */
    const tileSpinnerElRef = useRef(null);
    /** @type {MutableRefObject<number>} aktuell ladende Kacheln (pending) */
    const tilesPendingRef = useRef(0);
    /** @type {MutableRefObject<number>} in der aktuellen Lade-Session fertig geladene Kacheln */
    const tilesLoadedRef = useRef(0);
    /** @type {MutableRefObject<number>} zuletzt gezeichnete Stufe (0..STEPS), -1 = neu zeichnen */
    const lastTileStepsRef = useRef(-1);
    /** @type {MutableRefObject<number|null>} 200-ms-Timer bis zum Einblenden */
    const tileSpinnerDelayRef = useRef(null);
    /** @type {MutableRefObject<number|null>} Sicherheits-Timeout (Spinner nie unbegrenzt) */
    const tileSpinnerSafetyRef = useRef(null);
    /** @type {WeakSet<object>} Sources, an denen die Zähler-Listener bereits hängen */
    const tileCountedSourcesRef = useRef(new WeakSet());

    /**
     * Prüft, ob der Zeiger im Bereich des Maßstabsbalkens oder des Maßstab-Popups liegt
     * (inkl. kleinem Rand). Robuster als ein reiner DOM-Target-Check, da der Balken oft
     * über dem Karten-Canvas „schwebt" und das Pointer-Target dann der Canvas ist.
     * @param {import("ol/MapBrowserEvent").default} evt
     * @returns {boolean}
     */
    function isOverScaleRegion(evt) {
        if (isPointerOverScale(evt)) return true;
        const oe = evt?.originalEvent;
        if (!oe || oe.clientX == null || oe.clientY == null) return false;
        const margin = 10;
        return scaleRegionElsRef.current.some((el) => {
            if (!el || el.style?.display === "none") return false;
            const r = el.getBoundingClientRect();
            if (!r || (r.width === 0 && r.height === 0)) return false;
            return (
                oe.clientX >= r.left - margin &&
                oe.clientX <= r.right + margin &&
                oe.clientY >= r.top - margin &&
                oe.clientY <= r.bottom + margin
            );
        });
    }

    /**
     * Karten-Tooltips/Hover unterdrücken: während der PDF-Generierung generell,
     * sonst nur im Maßstabs-Bereich (Balken/Popup).
     * @param {import("ol/MapBrowserEvent").default} evt
     * @returns {boolean}
     */
    function shouldSuppressMapPointer(evt) {
        return pdfGeneratingRef.current || isOverScaleRegion(evt);
    }

    const {t, language} = useRevaTranslation();

    const dispatch = useDispatch();

    /**
     * @param {Map} map
     * @param {Object} eventHandlers
     */
    function registerMapEvents(map, eventHandlers) {
        if (!eventHandlers) {
            return;
        }
        if (eventHandlers.hover_feature_event) {
            logMapDebug("Registering Movein event ", eventHandlers.hover_feature_event);
            map.on("pointermove", (evt) => {
                if (shouldSuppressMapPointer(evt)) return;
                eventHandlersRef.current?.hover_feature_event?.(map, evt);
            });
        }
        if (eventHandlers.click_event) {
            logMapDebug("Registering Click event ", eventHandlers.click_event);
            map.on("click", (evt) => {
                if (shouldSuppressMapPointer(evt)) return;
                eventHandlersRef.current?.click_event?.(map, evt);
            });
        }
        if (eventHandlers.fade_out_event) {
            logMapDebug("Registering Fade out event ", eventHandlers.fade_out_event);
            map.getTargetElement().addEventListener("pointerleave", () =>
                eventHandlersRef.current?.fade_out_event?.(map),
            );
        }
        if (eventHandlers.moveend_event) {
            logMapDebug("Registering MoveEnd event ", eventHandlers.moveend_event);
            map.on("moveend", () => eventHandlersRef.current?.moveend_event?.(map));
        }
    }

    // --- Kachel-Lade-Fortschrittsbalken ---
    const isBarVisible = () => !!tileSpinnerElRef.current && tileSpinnerElRef.current.style.display !== "none";
    const updateProgressBar = () => {
        const el = tileSpinnerElRef.current;
        // Hot Path (läuft je Tile-Event): nur arbeiten, wenn der Balken sichtbar ist. Beim
        // Einblenden ruft der Delay-Callback updateProgressBar erneut -> wird dann frisch gezeichnet.
        if (!el || el.style.display === "none") return;
        const total = tilesLoadedRef.current + tilesPendingRef.current;
        const pct = total > 0 ? tilesLoadedRef.current / total : 0;
        // Nicht-linear quantisieren (Anteil^γ): erste Segmente = mehr Kacheln, hintere = weniger,
        // damit eine langsame letzte Kachel nicht als 99% stehen bleibt.
        const weighted = pct ** TILE_PROGRESS_GAMMA;
        const steps = Math.max(0, Math.min(TILE_PROGRESS_STEPS, Math.round(weighted * TILE_PROGRESS_STEPS)));
        // Stufe ändert sich max. STEPS-mal je Ladevorgang -> DOM/Übersetzung nur bei Änderung anfassen.
        if (steps === lastTileStepsRef.current) return;
        lastTileStepsRef.current = steps;
        for (let i = 0; i < el.children.length; i++) {
            el.children[i].classList.toggle("filled", i < steps);
        }
        // Hover-Tooltip: synchron mit den Segmenten (10%-Schritte), aber nie 100% — solange der
        // Spinner sichtbar ist, ist noch nicht alles fertig; daher auf 95% deckeln.
        const percent = Math.min(95, steps * (100 / TILE_PROGRESS_STEPS));
        el.title = t("general.map.tilesLoaded", {percent});
    };
    const hideTileProgress = () => {
        if (tileSpinnerDelayRef.current != null) {
            clearTimeout(tileSpinnerDelayRef.current);
            tileSpinnerDelayRef.current = null;
        }
        if (tileSpinnerSafetyRef.current != null) {
            clearTimeout(tileSpinnerSafetyRef.current);
            tileSpinnerSafetyRef.current = null;
        }
        if (tileSpinnerElRef.current) tileSpinnerElRef.current.style.display = "none";
        lastTileStepsRef.current = -1; // beim nächsten Einblenden frisch zeichnen
    };
    const onTileLoadStart = () => {
        // Neue Lade-Session (vorher nichts pending, Balken nicht sichtbar) → Zähler zurücksetzen.
        if (tilesPendingRef.current === 0 && !isBarVisible()) tilesLoadedRef.current = 0;
        tilesPendingRef.current += 1;
        // Balken nach kurzer Verzögerung einblenden (schnelle/gecachte Loads flackern so nicht).
        if (tileSpinnerDelayRef.current == null && !isBarVisible()) {
            tileSpinnerDelayRef.current = window.setTimeout(() => {
                tileSpinnerDelayRef.current = null;
                if (tilesPendingRef.current > 0 && tileSpinnerElRef.current) {
                    tileSpinnerElRef.current.style.display = "block";
                    updateProgressBar();
                    // Hard-Cap: spätestens nach 30 s ausblenden.
                    tileSpinnerSafetyRef.current = window.setTimeout(hideTileProgress, TILE_PROGRESS_MAX_MS);
                }
            }, TILE_PROGRESS_DELAY_MS);
        }
        updateProgressBar();
    };
    const onTileLoadDone = () => {
        tilesPendingRef.current = Math.max(0, tilesPendingRef.current - 1);
        tilesLoadedRef.current += 1;
        updateProgressBar();
        // Fertig, sobald keine Kacheln mehr fehlen.
        if (tilesPendingRef.current === 0) hideTileProgress();
    };
    // Bei jedem Layer-Wechsel (Klick) den Fortschritt neu bei 0 beginnen — auch wenn der Balken
    // gerade noch läuft: der Anteil zählt dann frisch über die verbleibenden Kacheln, und die
    // 30-s-Frist startet neu.
    const resetTileProgress = () => {
        tilesLoadedRef.current = 0;
        updateProgressBar();
        if (isBarVisible() && tileSpinnerSafetyRef.current != null) {
            clearTimeout(tileSpinnerSafetyRef.current);
            tileSpinnerSafetyRef.current = window.setTimeout(hideTileProgress, TILE_PROGRESS_MAX_MS);
        }
    };
    const attachTileSpinner = (map) => {
        if (!tileSpinnerElRef.current) {
            const el = document.createElement("div");
            el.className = "tile-loading-overlay";
            el.style.display = "none";
            for (let i = 0; i < TILE_PROGRESS_STEPS; i++) {
                el.appendChild(document.createElement("div")).className = "tile-loading-step";
            }
            map.getViewport().appendChild(el);
            tileSpinnerElRef.current = el;
        }
        // Zähler-Listener an alle (neuen) Tile-Sources hängen — idempotent via WeakSet.
        // Gilt für JEDEN Tile-Layer (Hintergrund, Lärm-, Emissions-, WMTS-Layer).
        map.getAllLayers().forEach((layer) => {
            const source = typeof layer.getSource === "function" ? layer.getSource() : null;
            if (!source || typeof source.on !== "function" || typeof source.getTile !== "function") return;
            if (tileCountedSourcesRef.current.has(source)) return;
            tileCountedSourcesRef.current.add(source);
            source.on("tileloadstart", onTileLoadStart);
            source.on("tileloadend", onTileLoadDone);
            source.on("tileloaderror", onTileLoadDone);
        });
    };

    // Timer beim Unmount aufräumen.
    useEffect(() => hideTileProgress, []);

    useEffect(() => {
        if (mapModel) {
            if (!mapRef.current) {
                // Create new map
                const map = new Map({
                    target: mapDivRef.current || undefined,
                    layers: [],
                    view: createView(mapModel.view),
                });

                createLayers(map, dispatch, mapModel.layers);
                if (mapModel.switcher?.enabled) {
                    const layerSwitcher = new LayerSwitcher(mapModel.switcher.options || {});
                    map.addControl(layerSwitcher);
                }
                // Enable ScaleLine if required
                if (mapModel.scaleLine?.enabled) {
                    const scaleLineDefaults = {
                        units: mapModel.scaleLine?.units || "metric",
                        bar: true,
                        steps: 4,
                        text: true,
                        minWidth: 200,
                    };
                    const scaleLine = new RoundedScaleLine(mapModel.scaleLine.options || scaleLineDefaults);
                    // OL ScaleLine hardcodes pointer-events:none on its element.
                    // Override so click events reach our handler for the scale popup.
                    scaleLine.element.style.pointerEvents = "auto";
                    scaleLine.element.style.paddingRight = "12px";
                    scaleLine.element.title = t("general.map.scaleLineTooltip");
                    map.addControl(scaleLine);
                    scaleRegionElsRef.current.push(scaleLine.element);

                    // Create scale popup and append it to the map viewport.
                    // This keeps the popup inside OL's DOM — no React state needed.
                    const scalePopup = createScalePopup();
                    map.getViewport().appendChild(scalePopup.element);
                    scaleRegionElsRef.current.push(scalePopup.element);

                    scaleLine.element.addEventListener("click", () => {
                        scalePopup.show(map, scaleLine.element);
                    });

                    scaleLine.element.addEventListener(
                        "wheel",
                        (e) => {
                            map.getViewport().dispatchEvent(new WheelEvent("wheel", e));
                        },
                        {passive: true},
                    );
                }
                // Enable draw Interaction if required
                if (mapModel.interaction?.enabled) {
                    if (mapModel.interaction.layer) {
                        map.addLayer(mapModel.interaction.layer);
                    }
                    const drawInteraction = new Draw(mapModel.interaction.options || {type: "Polygon"});
                    if (mapModel.interaction.drawFunction instanceof Function) {
                        mapModel.interaction.drawFunction(drawInteraction);
                    }
                    map.addInteraction(drawInteraction);
                    activeInteractionRef.current = drawInteraction;
                    activeInteractionLayerRef.current = mapModel.interaction.layer || null;
                }
                if (legendOverlayRef) {
                    const buttonLabel = t("general.map.note");
                    const legendControl = new OpenLayersLegendControl({
                        legendOverlayRef,
                        buttonLabel: legendOptions?.buttonLabel || buttonLabel,
                        visible: legendOptions?.visible === true,
                    });
                    legendControlRef.current = legendControl;
                    map.addControl(legendControl);
                }
                // "Zurück zum Pin": erscheint links oben, sobald der Adress-Marker
                // aus dem Sichtbereich gewandert ist; Klick zentriert auf den Marker
                // mit dem Default-Zoom dieser Karte.
                map.addControl(
                    new OpenLayersPinControl({
                        title: t("general.map.recenterPin"),
                        zoom: mapModel.view.zoom,
                        resolution: mapModel.view.resolution,
                    }),
                );
                if (tooltipOverlayRef) {
                    map.addOverlay(
                        new Overlay({
                            element: tooltipOverlayRef.current,
                            offset: [0, -10],
                            positioning: "bottom-center",
                            id: "tooltipOverlay",
                        }),
                    );
                }
                registerMapEvents(map, mapModel.eventHandlers);
                mapRef.current = map;
                attachTileSpinner(map);
                logMapDebug(`Created new Map (target=${mapDivRef.current}):`, map);
            } else {
                // Update map
                const map = mapRef.current;

                // Draw-Interaction ein-/ausschalten, falls sich mapModel.interaction seit dem
                // letzten Durchlauf geändert hat (z.B. ein Mess-Modus wird nachträglich auf einer
                // bereits gemounteten Karte an-/abgeschaltet, statt die Karte neu zu erzeugen).
                const wantsInteraction = !!mapModel.interaction?.enabled;
                const hasInteraction = !!activeInteractionRef.current;
                if (wantsInteraction && !hasInteraction) {
                    if (mapModel.interaction.layer) {
                        map.addLayer(mapModel.interaction.layer);
                    }
                    const drawInteraction = new Draw(mapModel.interaction.options || {type: "Polygon"});
                    if (mapModel.interaction.drawFunction instanceof Function) {
                        mapModel.interaction.drawFunction(drawInteraction);
                    }
                    map.addInteraction(drawInteraction);
                    activeInteractionRef.current = drawInteraction;
                    activeInteractionLayerRef.current = mapModel.interaction.layer || null;
                } else if (!wantsInteraction && hasInteraction) {
                    map.removeInteraction(activeInteractionRef.current);
                    if (activeInteractionLayerRef.current) {
                        map.removeLayer(activeInteractionLayerRef.current);
                    }
                    activeInteractionRef.current = null;
                    activeInteractionLayerRef.current = null;
                }

                const layersChanged = updateLayers(map, dispatch, mapModel.layers);
                attachTileSpinner(map);
                // Layer-Wechsel (Klick) → Fortschritt neu bei 0 beginnen.
                if (layersChanged) resetTileProgress();
                const willUpdate = layersChanged || !mapModel.view.fixedZoom;
                if (willUpdate) {
                    updateView(map, mapModel.view, layersChanged);
                }
                logMapDebug(`Updated Map (target=${mapDivRef.current}):`, map);
                mapModel.eventHandlers?.map_update_event?.(map);
            }
        }

        return () => {
            if (mapRef.current?.getTarget() && !mapRef.current) {
                logMapDebug("Clearing target of", mapRef.current);
                mapRef.current.setTarget(null);
            }
        };
    }, [mapDivRef, mapModel, dispatch, legendOverlayRef, t, tooltipOverlayRef, legendOptions?.visible]);

    useEffect(() => {
        if (legendControlRef.current) {
            const buttonLabel = legendOptions?.buttonLabel ?? t("general.map.legend");
            legendControlRef.current.updateButtonLabel(buttonLabel);
        }
    }, [language, legendControlRef, t]);

    // Legenden-Button/Overlay zur Laufzeit ein-/ausblenden, ohne die Karte neu zu mounten.
    // Nur wirksam, wenn der Aufrufer buttonVisible explizit setzt (sonst keine Änderung für
    // Tabs, die das Feld nicht nutzen).
    useEffect(() => {
        if (legendOptions?.buttonVisible === undefined) return;
        legendControlRef.current?.setVisible?.(legendOptions.buttonVisible);
    }, [legendOptions?.buttonVisible]);

    useLayoutEffect(() => {
        if (mapRef.current && mapDivRef.current && mapRef.current.getTarget() !== mapDivRef.current) {
            mapRef.current.setTarget(mapDivRef.current);
            logMapDebug(`Setting map target to ${mapDivRef.current}`);
        }
    }, [mapDivRef]);

    // Rote PDF-Druckbereich-Vorschau: bewusst als bildschirm-fixes DOM-Overlay (KEIN
    // OpenLayers-Layer), damit die Box beim Pannen/Zoomen komplett unbeweglich mittig
    // über der Karte liegt. Da die interaktive Karte und das PDF dieselbe Auflösung
    // nutzen und der Export stets auf das aktuelle Kartenzentrum (= Viewport-Mitte)
    // zentriert, entspricht der Druckbereich exakt pixelWidth×pixelHeight CSS-Pixeln
    // mittig im Viewport — unabhängig von Pan/Zoom.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !pdfPreview || pdfPreview.nonce <= 0) return;

        /** Laufende Fade-/Hide-Timer stoppen. */
        const clearTimers = () => {
            if (pdfPreviewFadeStartRef.current != null) {
                clearTimeout(pdfPreviewFadeStartRef.current);
                pdfPreviewFadeStartRef.current = null;
            }
            if (pdfPreviewHideRef.current != null) {
                clearTimeout(pdfPreviewHideRef.current);
                pdfPreviewHideRef.current = null;
            }
        };

        clearTimers();

        // Overlay-Element einmalig anlegen und mittig in den Karten-Viewport hängen.
        if (!pdfPreviewElRef.current) {
            const el = document.createElement("div");
            el.className = "pdf-print-area-preview";
            el.style.position = "absolute";
            el.style.left = "50%";
            el.style.top = "50%";
            el.style.transform = "translate(-50%, -50%)";
            el.style.boxSizing = "border-box";
            el.style.border = "4px solid var(--primary-color)";
            el.style.background = "transparent";
            el.style.pointerEvents = "none";
            el.style.zIndex = "600";

            const label = document.createElement("span");
            label.className = "pdf-print-area-preview-label";
            label.textContent = "PDF-Druckbereich";
            label.style.position = "absolute";
            label.style.left = "0";
            label.style.top = "0";
            label.style.padding = "1px 5px";
            label.style.font = "bold 13px Calibri, sans-serif";
            label.style.lineHeight = "1.4";
            label.style.color = "#fff";
            label.style.background = "var(--primary-color)";
            label.style.whiteSpace = "nowrap";
            el.appendChild(label);

            // Großes monochromes PDF-Icon, nur während der Generierung sichtbar.
            const icon = document.createElement("i");
            icon.className = "pi pi-file-pdf pdf-print-area-preview-icon";
            icon.style.position = "absolute";
            icon.style.left = "50%";
            icon.style.top = "50%";
            icon.style.transform = "translate(-50%, -50%)";
            icon.style.fontSize = "192px";
            icon.style.lineHeight = "1";
            icon.style.color = "var(--primary-color)";
            icon.style.display = "none";
            el.appendChild(icon);

            // Hinweis, wenn der Druckbereich größer als der Viewport ist und die Box
            // über den sichtbaren Kartenrand hinausragt. Sitzt am (sichtbaren) oberen
            // Rand der Box, mittig; Positionierung/Sichtbarkeit im Body je Lauf.
            const hint = document.createElement("div");
            hint.className = "pdf-print-area-preview-hint";
            const {name: browserName, shortcut: zoomOutShortcut} = getBrowserZoomOutShortcut();
            hint.textContent =
                "Zu geringe Kartenhöhe/-breite, um den gesamten Druckbereich anzuzeigen. Das PDF wird aber vollständig erzeugt. " +
                `Tipp: ${zoomOutShortcut} verkleinert in ${browserName} die Ansicht, sodass der ganze Druckbereich sichtbar wird.`;
            hint.style.position = "absolute";
            hint.style.transform = "translateX(-50%)";
            hint.style.maxWidth = "min(80%, 360px)";
            hint.style.padding = "2px 7px";
            hint.style.font = "11px Calibri, sans-serif";
            hint.style.lineHeight = "1.25";
            hint.style.textAlign = "center";
            hint.style.color = "rgba(255,255,255,.9)";
            hint.style.background = "rgba(0,0,0,.4)";
            hint.style.borderRadius = "3px";
            hint.style.pointerEvents = "none";
            hint.style.display = "none";
            el.appendChild(hint);

            // Prominente Handlungsaufforderung, mittig in der oberen Box-Hälfte: das ist
            // der eigentliche Zweck der Box — den Ausschnitt fürs PDF passend zu wählen.
            const instruction = document.createElement("div");
            instruction.className = "pdf-print-area-preview-instruction";
            instruction.textContent = "Bei Bedarf den Kartenausschnitt für das PDF anpassen.";
            instruction.style.position = "absolute";
            instruction.style.transform = "translateX(-50%)";
            instruction.style.maxWidth = "min(90%, 520px)";
            instruction.style.padding = "6px 12px";
            instruction.style.font = "bold 16px Calibri, sans-serif";
            instruction.style.lineHeight = "1.3";
            instruction.style.textAlign = "center";
            instruction.style.color = "#fff";
            instruction.style.background = "var(--primary-color)";
            instruction.style.borderRadius = "4px";
            instruction.style.pointerEvents = "none";
            el.appendChild(instruction);

            // Klickbare Fläche(n) in der Box-Mitte: löst denselben Download aus wie der
            // „PDF drucken"-Button. Die Box selbst hat pointer-events:none (damit man
            // durch sie hindurch pannen kann) — diese Gruppe schaltet sie lokal wieder ein.
            // Ein Button je {@link PdfPreviewInfo.actions}-Eintrag (mehrere, wenn sich
            // mehrere Export-Buttons dieselbe Karte teilen, z.B. Übersicht + Kartenset).
            const triggerGroup = document.createElement("div");
            triggerGroup.className = "pdf-print-area-preview-trigger-group";
            triggerGroup.style.position = "absolute";
            triggerGroup.style.left = "50%";
            triggerGroup.style.top = "50%";
            triggerGroup.style.transform = "translate(-50%, -50%)";
            triggerGroup.style.display = "flex";
            triggerGroup.style.flexDirection = "row";
            triggerGroup.style.alignItems = "center";
            triggerGroup.style.gap = "12px";
            el.appendChild(triggerGroup);

            // Schließen-Kreuz oben rechts: immer sichtbar (wie das Label) und blendet die
            // Box sofort aus — dieselbe Wirkung wie das normale Ausblenden. pointer-events
            // lokal wieder aktiviert (die Box selbst ist durchklickbar).
            const close = document.createElement("button");
            close.type = "button";
            close.className = "pdf-print-area-preview-close";
            close.setAttribute("aria-label", "Druckbereich-Vorschau schließen");
            close.title = "Schließen (Esc)";
            close.textContent = "×";
            close.style.position = "absolute";
            close.style.right = "0";
            close.style.top = "0";
            close.style.width = "24px";
            close.style.height = "24px";
            close.style.display = "flex";
            close.style.alignItems = "center";
            close.style.justifyContent = "center";
            close.style.padding = "0";
            close.style.border = "none";
            close.style.cursor = "pointer";
            close.style.color = "#fff";
            close.style.background = "var(--primary-color)";
            close.style.font = "bold 18px/1 Calibri, sans-serif";
            close.style.pointerEvents = "auto";
            el.appendChild(close);

            pdfPreviewElRef.current = el;
            map.getViewport().appendChild(el);
        }

        const el = pdfPreviewElRef.current;
        const label = el.querySelector(".pdf-print-area-preview-label");
        const icon = el.querySelector(".pdf-print-area-preview-icon");
        const hint = el.querySelector(".pdf-print-area-preview-hint");
        const instruction = el.querySelector(".pdf-print-area-preview-instruction");
        const triggerGroup = el.querySelector(".pdf-print-area-preview-trigger-group");
        const close = el.querySelector(".pdf-print-area-preview-close");
        if (icon) icon.style.display = "none"; // Default: nur während der Generierung zeigen

        /** Box nach 8 s über 2 s ausblenden (nur im Nicht-Generierungs-Zustand). */
        const scheduleFade = () => {
            clearTimers();
            pdfPreviewFadeStartRef.current = window.setTimeout(() => {
                pdfPreviewFadeStartRef.current = null;
                el.style.transition = "opacity 2s ease-out";
                el.style.opacity = "0";
            }, 8000);
            pdfPreviewHideRef.current = window.setTimeout(() => {
                pdfPreviewHideRef.current = null;
                el.style.display = "none";
            }, 10000);
        };

        /** Vorschau sofort ausblenden — dieselbe Wirkung wie das Auto-Ausblenden. */
        const dismiss = () => {
            if (pdfPreview.generating) return; // während der Generierung nicht schließbar
            clearTimers();
            el.style.transition = "none";
            el.style.opacity = "1";
            el.style.display = "none";
            dispatch(hidePdfPreview());
        };

        // Box zeigt EXAKT den PDF-Ausschnitt (pixelWidth×pixelHeight, 1:1, KEINE
        // Verkleinerung). Passt der Ausschnitt nicht in den Viewport, ragt die Box
        // bewusst über den sichtbaren Kartenrand hinaus (und wird dort abgeschnitten) —
        // nur so entspricht die Box exakt dem, was später im PDF steht.
        // Als Funktion, damit Layout + „ragt-über-Viewport"-Hinweis auch bei Größen-/
        // Zoom-Änderungen des Browsers (Strg +/−) neu berechnet werden.
        const relayout = () => {
            const [vw = 0, vh = 0] = map.getSize() || [];
            el.style.width = `${pdfPreview.pixelWidth}px`;
            el.style.height = `${pdfPreview.pixelHeight}px`;
            // Sichtbaren oberen/linken Rand der (evtl. überstehenden) Box bestimmen, damit
            // Label, Kreuz und Hinweis nicht außerhalb des Viewports landen.
            const overflowX = vw > 0 ? Math.max(0, (pdfPreview.pixelWidth - vw) / 2) : 0;
            const overflowY = vh > 0 ? Math.max(0, (pdfPreview.pixelHeight - vh) / 2) : 0;
            const exceedsViewport = overflowX > 0 || overflowY > 0;
            if (label) {
                label.style.left = `${overflowX}px`;
                label.style.top = `${overflowY}px`;
            }
            if (close) {
                close.style.right = `${overflowX}px`;
                close.style.top = `${overflowY}px`;
            }
            if (hint) {
                // Dezent am unteren (sichtbaren) Rand der Box; overflowY hält sie im Viewport.
                hint.style.left = "50%";
                hint.style.top = "auto";
                hint.style.bottom = `${overflowY + 10}px`;
                // Während der Generierung bleibt der Hinweis ohnehin aus (siehe unten).
                if (!pdfPreview.generating) hint.style.display = exceedsViewport ? "block" : "none";
            }
            if (instruction) {
                // Obere Box-Hälfte, mittig. Bei überstehender Box nach unten schieben, damit sie
                // im sichtbaren Bereich bleibt und das Label oben nicht überlappt.
                instruction.style.left = "50%";
                instruction.style.top = `${Math.max(Math.round(pdfPreview.pixelHeight * 0.22), overflowY + 12)}px`;
            }
            if (triggerGroup) {
                // Button-Gruppe in die untere Box-Hälfte, damit die Kartenmitte (Adress-Marker
                // in der Box-Mitte) frei sichtbar bleibt; dabei im sichtbaren Bereich halten.
                const markerY = pdfPreview.pixelHeight / 2;
                const visibleBottom = overflowY + (vh || pdfPreview.pixelHeight);
                triggerGroup.style.top = `${Math.min(markerY + 120, visibleBottom - 64)}px`;
            }
        };

        relayout();

        // Browser-Zoom (Strg/⌘ +/−) und Fenster-Resize lösen ein Neu-Layout aus, damit der
        // „ragt über den Viewport hinaus"-Hinweis korrekt ein-/ausblendet. Vorher die
        // Kartengröße aktualisieren, sonst liefert map.getSize() den alten Wert.
        const onViewportResize = () => {
            map.updateSize();
            relayout();
            // Ist die Box gerade sichtbar (normaler Hover-Zustand), den Ausblend-Timer
            // wieder auf volle 10 s zurücksetzen — der Nutzer justiert ja gerade.
            if (!pdfPreview.generating && el.style.display !== "none") {
                el.style.transition = "none";
                el.style.opacity = "1";
                scheduleFade();
            }
        };
        window.addEventListener("resize", onViewportResize);
        const visualViewport = window.visualViewport;
        if (visualViewport) visualViewport.addEventListener("resize", onViewportResize);

        // Esc schließt die Vorschau (wie das ×-Kreuz oben rechts).
        const onKeyDown = (e) => {
            if (e.key === "Escape") dismiss();
        };
        window.addEventListener("keydown", onKeyDown);

        /** Aufräumen inkl. aller Listener beim Neu-Lauf/Unmount. */
        const cleanup = () => {
            clearTimers();
            window.removeEventListener("resize", onViewportResize);
            if (visualViewport) visualViewport.removeEventListener("resize", onViewportResize);
            window.removeEventListener("keydown", onKeyDown);
        };

        // ×-Kreuz: schließt die Vorschau sofort.
        if (close) {
            close.onclick = (ev) => {
                ev.stopPropagation();
                dismiss();
            };
        }

        // Klick-Flächen: pro Aktion frisch aufbauen (Anzahl/Beschriftung kann sich zwischen
        // Läufen ändern, z.B. beim Wechsel des gehoverten Buttons).
        if (triggerGroup) {
            triggerGroup.replaceChildren();
            for (const action of pdfPreview.actions) {
                const button = document.createElement("button");
                button.type = "button";
                button.className = "pdf-print-area-preview-trigger";
                button.style.display = "inline-flex";
                button.style.flexDirection = "column";
                button.style.alignItems = "center";
                button.style.gap = "4px";
                button.style.padding = "9px 13px";
                button.style.border = "none";
                button.style.borderRadius = "8px";
                button.style.cursor = "pointer";
                button.style.color = "#fff";
                button.style.background = "var(--primary-color)";
                button.style.boxShadow = "0 2px 8px rgba(0,0,0,.3)";
                button.style.font = "bold 12px Calibri, sans-serif";
                button.style.pointerEvents = "auto";

                const buttonIcon = document.createElement("i");
                buttonIcon.className = "pi pi-file-pdf";
                buttonIcon.style.fontSize = "24px";
                buttonIcon.style.lineHeight = "1";
                button.appendChild(buttonIcon);

                const buttonLabel = document.createElement("span");
                buttonLabel.textContent = action.label;
                button.appendChild(buttonLabel);

                button.onclick = (ev) => {
                    ev.stopPropagation();
                    clearTimers();
                    el.style.transition = "none";
                    el.style.opacity = "1";
                    dispatch(downloadPdfForType(action.type, action.orientation)).catch((e) => console.error(e));
                };
                // Solange die Maus auf einer Fläche liegt, Box sichtbar halten; beim
                // Verlassen wieder normal ausblenden.
                button.onmouseenter = () => {
                    clearTimers();
                    el.style.transition = "none";
                    el.style.opacity = "1";
                };
                button.onmouseleave = () => scheduleFade();

                triggerGroup.appendChild(button);
            }
        }

        if (pdfPreview.generating) {
            // Während der PDF-Generierung: der rote Rahmen bleibt solide (opacity 1),
            // nur die FÜLLFLÄCHE pulsiert halbtransparent. KEIN Auto-Fade. Pannen aus.
            pdfGeneratingRef.current = true;
            ensurePdfPreviewBreatheKeyframes();
            setDragPanActive(map, false);
            el.style.display = "block";
            el.style.transition = "none";
            el.style.opacity = "1";
            el.style.animation = "revaPdfPreviewBreathe 1.6s ease-in-out infinite";
            if (label) label.textContent = "Karten-PDF wird generiert …";
            if (icon) icon.style.display = "block";
            if (triggerGroup) triggerGroup.style.display = "none"; // während Generierung keine Klick-Fläche
            if (close) close.style.display = "none"; // während Generierung nicht schließbar
            if (hint) hint.style.display = "none";
            if (instruction) instruction.style.display = "none";
            return cleanup;
        }

        // generating === false: Pannen + Tooltips wieder erlauben, Atem-Animation/Füllung zurücksetzen.
        const wasGenerating = pdfGeneratingRef.current;
        pdfGeneratingRef.current = false;
        setDragPanActive(map, true);
        el.style.animation = "none";
        el.style.background = "transparent";
        if (triggerGroup) triggerGroup.style.display = "flex";
        if (close) close.style.display = "flex";
        if (instruction) instruction.style.display = "block";

        if (wasGenerating) {
            // Generierung gerade beendet -> direkt verschwinden.
            el.style.display = "none";
            el.style.opacity = "1";
            if (label) label.textContent = "PDF-Druckbereich";
            return cleanup;
        }

        // Normaler Hover: voll sichtbar, nach 8 s über 2 s ausblenden.
        if (label) label.textContent = "PDF-Druckbereich";
        el.style.transition = "none";
        el.style.opacity = "1";
        el.style.display = "block";
        void el.offsetWidth; // Reflow erzwingen, damit die folgende Transition greift

        scheduleFade();

        return cleanup;
        // nonce/generating triggern neue Durchläufe; pixelWidth/-Height sind im Closure aktuell.
    }, [pdfPreview?.nonce, pdfPreview?.pixelWidth, pdfPreview?.pixelHeight, pdfPreview?.generating]);

    return mapRef;
}

/**
 * @typedef {Object} LegendOptions
 * @property {string} buttonLabel
 * @property {boolean} visible
 * @property {boolean} [dismissable] false = Legende schließt nicht bei Klick außerhalb, nur über den Button (Default true)
 */

/** @typedef {import("../hooks/usePdfPreview").PdfPreviewInfo} PdfPreviewInfo */

/**
 * OpenLayer Map component.
 * @param {MapModel|null} model the map model describing the layers
 * @param {JSX.Element|null} legend the (optional) map legend element
 * @param {LegendOptions|null} legendOptions the optional options for the legend
 * @param {PdfPreviewInfo|null} [pdfPreview] optionale PDF-Karten-Vorschau (rote Box beim Button-Hover)
 * @return {JSX.Element|null}
 */
function OpenLayersMap({model, legend, legendOptions, pdfPreview}) {
    /** @type {MutableRefObject<HTMLDivElement|null>} */
    const mapDivRef = useRef(null);
    /** @type {MutableRefObject<OverlayPanel|null>} */
    const legendOverlayRef = useRef(null);
    const tooltipOverlayRef = useRef(null);

    useOpenLayersMap(mapDivRef, model, tooltipOverlayRef, legend ? legendOverlayRef : null, legendOptions, pdfPreview);

    if (!model) return null;

    if (!legend)
        return (
            <div ref={mapDivRef} className="OpenLayersMap">
                <div
                    ref={tooltipOverlayRef}
                    className="bg-surface-50 dark:bg-surface-800 p-2 rounded-border border border-surface-300 dark:border-surface-500 p-component text-color"
                />
            </div>
        );

    return (
        <div ref={mapDivRef} className="OpenLayersMap">
            <OverlayPanel
                style={{zIndex: 100}}
                ref={legendOverlayRef}
                className="OpenLayersLegendOverlay"
                dismissable={legendOptions?.dismissable !== false}
            >
                {legend}
            </OverlayPanel>
            <div
                ref={tooltipOverlayRef}
                className="bg-surface-50 dark:bg-surface-800 p-2 rounded-border border border-surface-300 dark:border-surface-500 p-component text-color"
            />
        </div>
    );
}

export default OpenLayersMap;
